import { computed, defineComponent, ref, watch, type SetupContext } from "vue";
import html_iway from "./template.iway.html?raw";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}

const pad2 = (value: number): string => String(value).padStart(2, "0");

// Anzahl Tage eines Monats (month 1-basiert), berücksichtigt Schaltjahre
const daysInMonth = (year: number, month: number): number => new Date(year, month, 0).getDate();

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Wandelt einen gespeicherten ISO-Wert (YYYY-MM-DD) in die Anzeige "DD.MM.YYYY" um
const isoToDisplay = (iso: string | null): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso ?? "").trim());
  if (!match) {
    return "";
  }
  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
};

// Erweitert ein zweistelliges Jahr auf vier Stellen: Ist es (noch) nicht grösser als
// das aktuelle Jahr (in dessen Jahrhundert), wird das aktuelle Jahrhundert angenommen,
// sonst das vorherige - ein Geburtsjahr kann schliesslich nicht in der Zukunft liegen.
const expandTwoDigitYear = (twoDigitYear: number, today: Date): number => {
  const currentCentury = Math.floor(today.getFullYear() / 100) * 100;
  const currentYearInCentury = today.getFullYear() % 100;
  return twoDigitYear <= currentYearInCentury
    ? currentCentury + twoDigitYear
    : currentCentury - 100 + twoDigitYear;
};

interface ParsedDate {
  day: number;
  month: number;
  year: number;
}

// Parst die Anzeige "D.M.Y" (Tag/Monat 1-2-stellig, Jahr 1-4-stellig) und korrigiert sie
// zu einem gültigen, nicht in der Gegenwart oder Zukunft liegenden Datum:
// - Tag/Monat werden auf einen gültigen Bereich begrenzt (z.B. 30.2. -> 28.2.)
// - ein zweistelliges Jahr wird via expandTwoDigitYear aufgelöst
// - liegt das so entstandene Datum auf dem heutigen Tag oder danach, wird
//   (nur bei zweistelligem Jahr) ein Jahr abgezogen
const parseAndCorrect = (raw: string, today: Date): ParsedDate | null => {
  const parts = raw
    .split(".")
    .map((part) => part.trim())
    .filter((part) => 0 < part.length);
  if (3 != parts.length) {
    return null;
  }
  const [dayPart, monthPart, yearPart] = parts;
  if (!/^\d{1,2}$/.test(dayPart) || !/^\d{1,2}$/.test(monthPart) || !/^\d{1,4}$/.test(yearPart)) {
    return null;
  }

  const month = Math.min(Math.max(parseInt(monthPart, 10), 1), 12);

  const isShortYear = yearPart.length <= 2;
  let year = isShortYear ? expandTwoDigitYear(parseInt(yearPart, 10), today) : parseInt(yearPart, 10);

  let day = Math.min(Math.max(parseInt(dayPart, 10), 1), daysInMonth(year, month));

  if (isShortYear && startOfDay(new Date(year, month - 1, day)).getTime() >= startOfDay(today).getTime()) {
    year -= 1;
    day = Math.min(day, daysInMonth(year, month));
  }

  return { day, month, year };
};

let instanceCounter = 0;

interface Props {
  modelValue: string | null;
  label: string;
  error: string;
  className: string;
}

export default defineComponent({
  template: html,
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      required: true,
    },
    error: {
      type: String,
      default: "",
    },
    className: {
      type: String,
      default: "",
    },
  },
  emits: {
    "update:modelValue": (_value: string | null) => true,
    change: () => true,
  },
  setup(props: Props, { emit }: SetupContext) {
    // eindeutige ID, da diese Komponente mehrfach gleichzeitig gerendert werden kann
    const inputId = `birthdate-entry-${++instanceCounter}`;

    const displayValue = ref<string>(isoToDisplay(props.modelValue));

    watch(
      () => props.modelValue,
      (value) => {
        if (isoToDisplay(value) != displayValue.value) {
          displayValue.value = isoToDisplay(value);
        }
      },
    );

    // Während der Eingabe wird nicht validiert (nur formatiert) - Fehler kommen ausschliesslich vom Server
    const displayedError = computed(() => props.error);

    // Während der Eingabe wird nur bereinigt (erlaubte Trennzeichen -> "."), die eigentliche
    // Korrektur (Jahrhundert/Vergangenheit) erfolgt erst beim Verlassen des Feldes, da sie
    // ein vollständiges Datum benötigt
    const onInput = (evt: Event) => {
      const target = evt.target as HTMLInputElement;
      const sanitized = target.value
        .replace(/[/-]/g, ".")
        .replace(/[^0-9.]/g, "")
        .replace(/\.{2,}/g, ".");
      displayValue.value = sanitized;
      target.value = sanitized;
    };

    const onChange = () => {
      const corrected = parseAndCorrect(displayValue.value, new Date());
      if (corrected) {
        displayValue.value = `${pad2(corrected.day)}.${pad2(corrected.month)}.${corrected.year}`;
        emit("update:modelValue", `${corrected.year}-${pad2(corrected.month)}-${pad2(corrected.day)}`);
      } else if ("" == displayValue.value.trim()) {
        emit("update:modelValue", null);
      }
      emit("change");
    };

    return {
      inputId,
      displayValue,
      displayedError,
      onInput,
      onChange,
    };
  },
} as any);
